import {
  WSControl,
  WSControlGroup,
  WSControlGroupProps,
  WSForm
} from "@wingspanhq/fe-component-library";
import React from "react";
import { IDynamicFieldProps } from "../../types";
import { fieldLabel, isFieldRequired } from "../../utils/fieldRules";

// WSControlGroup's radio variant binds string option values, but a Boolean
// field's wire/schema value is a boolean (typed `bool` in the per-revision
// protos). This adapter converts at the component boundary so RHF,
// validation, and the submit payload all keep the boolean shape — the same
// pattern DynamicFieldEnum uses for its checkbox-array adapter.
type BooleanRadioGroupProps = Omit<
  Extract<WSControlGroupProps, { type: "radio" }>,
  "type" | "value" | "onChange" | "options"
> & {
  value?: boolean;
  onChange: (value: boolean) => void;
};

type BooleanRadioLabels = NonNullable<
  IDynamicFieldProps["field"]["ui"]
>["booleanLabels"];

// Catalog-authored option copy when provided (e.g. "Yes — add deductions" with
// a sublabel, per the Info-Architecture deductions design); plain Yes/No
// otherwise.
const booleanRadioOptions = (labels?: BooleanRadioLabels) => {
  const yes = {
    label: labels?.trueLabel ?? "Yes",
    value: "true",
    description: labels?.trueDescription
  };
  const no = {
    label: labels?.falseLabel ?? "No",
    value: "false",
    description: labels?.falseDescription
  };
  return labels?.falseFirst ? [no, yes] : [yes, no];
};

const WSControlGroupBooleanRadio: React.FC<
  BooleanRadioGroupProps & { booleanLabels?: BooleanRadioLabels }
> = ({ value, onChange, booleanLabels, ...rest }) => (
  <WSControlGroup
    {...rest}
    type="radio"
    options={booleanRadioOptions(booleanLabels)}
    // Unanswered stays unselected — be explicit so a cleared value never
    // matches an option.
    value={value === undefined || value === null ? "" : String(value)}
    onChange={(next: string) => onChange(next === "true")}
  />
);

export const DynamicFieldBoolean: React.FC<IDynamicFieldProps> = ({
  field,
  name,
  disabled,
  defaultValue
}) => {
  // A catalog Boolean presented as a yes/no question (ui.component RadioGroup,
  // per the Info-Architecture design for exemption / itemized deductions)
  // renders as a two-option radio group; Checkbox/Switch keep WSControl.
  if (field.ui?.component === "RadioGroup") {
    return (
      <WSForm.Field
        name={name}
        defaultValue={defaultValue}
        label={fieldLabel(field)}
        description={field.description ?? field.ui?.helpText}
        component={WSControlGroupBooleanRadio}
        componentProps={{
          size: "S",
          disabled,
          required: isFieldRequired(field),
          chip: true,
          booleanLabels: field.ui?.booleanLabels
        }}
      />
    );
  }

  return (
    <WSForm.Field
      name={name}
      defaultValue={defaultValue}
      component={WSControl}
      componentProps={{
        type: field.ui?.component === "Switch" ? "switch" : "checkbox",
        size: "S",
        label: field.label,
        description: field.description ?? field.ui?.helpText,
        disabled
      }}
    />
  );
};
